import { isEmpty } from 'lodash-es';

import { FabricUser } from 'src/components/bbc-atlaskit-editor';
import { encode } from 'src/components/conversation-provider/serializer';
import {
  ProviderConfig,
  ADF,
  InlineField,
  PlaceholderApiComment,
} from 'src/components/conversation-provider/types';
import {
  toBBUser,
  toConversation,
} from 'src/components/conversation-provider/utils';
import { ADD_COMMENT } from 'src/redux/pull-request/actions';
import { BucketDispatch } from 'src/types/state';
import authRequest, { jsonHeaders } from 'src/utils/fetch';
import { short12Hash } from 'src/utils/short-hash';

import { uuid } from '../internal/uuid';

/**
 * Creates a new Conversation and associates it with the containerId provided.
 */
export async function create(
  dispatch: BucketDispatch,
  user: FabricUser,
  localId: string,
  value: ADF,
  meta: InlineField,
  containerId: string,
  pending?: boolean,
  cslrHash?: string
) {
  // eslint-disable-next-line babel/no-invalid-this
  const { config } = this;
  const { urls, anchor, destRev } = config as ProviderConfig;

  const metaData: Partial<InlineField> = {
    ...meta,
  };

  if (metaData.to === null) {
    delete metaData.to;
  }

  if (metaData.from === null) {
    delete metaData.from;
  }

  if (metaData.from && metaData.to) {
    // For comment drifting, remove TO when both lines are provided (COREX-2173)
    delete metaData.to;
  }

  const hasMeta = !isEmpty(metaData);
  const timeNow = new Date().toISOString();
  const placeholderId = uuid.generate() as string;

  const placeholderComment: PlaceholderApiComment = {
    created_on: timeNow,
    updated_on: timeNow,
    content: {
      doc: value,
    },
    user: toBBUser(user),
    placeholderId,
    pending,
  };

  const inline = {} as InlineField;
  if (hasMeta) {
    if (metaData.path) {
      inline.path = metaData.path;
    }
    if (metaData.from) {
      inline.from = metaData.from;
    }
    if (metaData.to) {
      inline.to = metaData.to;
    }

    inline.base_rev = cslrHash;

    /* 
    Comments API serializes src_rev and dest_rev as part of the inline field.
    However, when creating comments, src_rev and dest_rev are expected outside of the inline field.
    Because of this we need to include src_rev and dest_rev within inline for the
    placeholder comment, but not for the request payload. 
    */
    placeholderComment.inline = inline;
    placeholderComment.inline = {
      ...placeholderComment.inline,
      src_rev: anchor,
      dest_rev: destRev,
    };
  }

  dispatch({
    type: ADD_COMMENT.REQUEST,
    payload: placeholderComment,
  });

  const bodyJSON: { [key: string]: any } = {
    content: { raw: encode(value) },
    inline: hasMeta ? inline : undefined,
    anchor: anchor && short12Hash(anchor),
    dest_rev: destRev && short12Hash(destRev),
  };

  // to avoid including "pending" field for commit comments
  if (pending) {
    bodyJSON.pending = true;
  }

  const response = await fetch(
    authRequest(urls.commentSave(), {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(bodyJSON),
    })
  );

  if (!response.ok) {
    throw Error(response.statusText);
  }

  const result = await response.json();
  // Never treat a comment we just created as outdated.
  // (See COREX-1933)
  if (result.inline && result.inline.outdated) {
    result.inline.outdated = false;
  }

  const conversation = toConversation([result], containerId, localId);
  if (conversation) {
    dispatch({
      type: ADD_COMMENT.SUCCESS,
      payload: {
        placeholderId,
        comment: result,
      },
    });
  }

  return conversation;
}
